/**
 * UI response → `InteractionOutcome`, the wire format the sandbox's gateway
 * resolver expects.
 *
 * One switch, one case per interaction kind, so the shape each kind owes the
 * SDK (AskUserQuestion's `{ questions, answers }`, hands' nonce echo,
 * permissions v2's scoped `addRule`) is stated in one readable place instead
 * of inline in the store action that calls it.
 */

import type { InteractionOutcome } from "@bash/shared";
import type { PendingInteraction } from "./types";
import type { InteractionResponse } from "./taskStoreTypes";

/**
 * Translate a user's UI response into an InteractionOutcome — the wire-format
 * the sandbox's gateway resolver expects. `pi` is passed so we can read the
 * current interaction's questions / toolkit to build the right `updatedInput`
 * for allow paths (SDK needs `{ questions, answers }` for AskUserQuestion, etc).
 */
export function buildInteractionOutcome(
  response: InteractionResponse,
  pi: PendingInteraction
): InteractionOutcome {
  switch (response.type) {
    case "question": {
      // SDK expects { questions, answers } back as the tool's result.
      const questions = pi.type === "question" ? pi.questions : [];
      return {
        behavior: "allow",
        updatedInput: { questions, answers: response.answers ?? {} },
      };
    }
    case "auth":
      return response.approved === false
        ? { behavior: "deny", reason: "user_declined" }
        : { behavior: "allow" };
    case "hands":
      // OS Hands P2.5a — approve echoes the request nonce back so the gate can
      // verify it (a nonce-less/mismatched allow is treated as declined). The
      // nonce rides the pending interaction, never the user's click.
      if (!response.approved)
        return { behavior: "deny", reason: "user_declined" };
      return {
        behavior: "allow",
        ...(pi.type === "hands" && pi.nonce ? { nonce: pi.nonce } : {}),
      };
    case "approval":
      return response.approved
        ? { behavior: "allow" }
        : { behavior: "deny", reason: "user_declined" };
    case "permission": {
      if (!response.approved)
        return { behavior: "deny", reason: "user_declined" };
      // Scoped grant (permissions v2): clone the first SDK-suggested rule and
      // override its destination to match the chosen scope — 'task' persists to
      // the session rules (destination:'session'), 'always' to the user's
      // settings (destination:'user'). Guarded on suggestions presence; 'once'
      // (or no suggestions) stays a bare allow, byte-identical to today.
      if (
        (response.scope === "task" || response.scope === "always") &&
        pi.type === "permission" &&
        pi.suggestions?.length
      ) {
        const destination = response.scope === "always" ? "user" : "session";
        return {
          behavior: "allow",
          addRule: { ...pi.suggestions[0], destination },
        };
      }
      return { behavior: "allow" };
    }
    case "plan_limit":
      // Engines T5: the card's answer is the choice itself; the server checks
      // it against the card it minted (400 invalid_choice otherwise, never a
      // guessed default here).
      return { behavior: "allow", updatedInput: { choice: response.choice } };
    default:
      // Phase 8: custom-kind interactions MUST NOT route through this helper.
      // Renderers call resolveInteractionApi directly. The default deny here
      // would silently turn a custom-kind Submit into a deny. If a future
      // store action ever passes a 'custom' type to this helper, that's a bug.
      return { behavior: "deny", reason: "user_declined" };
  }
}
